import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ApiError,
  enrollRecoveryCodes,
  fetchSignupStatus,
  passkeySignup,
  supportsPasskeys,
} from '../lib/api'
import { WebAuthnUserError } from '../lib/webauthnCeremony'
import { EmailCodePanel } from '../components/EmailCodePanel'

type Phase = 'loading' | 'closed' | 'form' | 'codes'

export function SignupPasskeyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('loading')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [codesAck, setCodesAck] = useState(false)
  const [skipWarned, setSkipWarned] = useState(false)
  const inFlight = useRef(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const st = await fetchSignupStatus()
        if (cancelled) return
        setPhase(st.public_signup_enabled ? 'form' : 'closed')
      } catch {
        if (!cancelled) setPhase('closed')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setError(null)
    if (!supportsPasskeys()) {
      setError(t('auth.webauthnUnsupported'))
      inFlight.current = false
      return
    }
    setBusy(true)
    try {
      await passkeySignup(email.trim() || undefined)
      setPhase('codes')
      try {
        const res = await enrollRecoveryCodes()
        setCodes(res.codes)
      } catch (enrollErr) {
        setError(enrollErr instanceof ApiError ? enrollErr.message : t('auth.codesFailed'))
      }
    } catch (err) {
      if (err instanceof WebAuthnUserError) {
        if (err.code === 'cancelled') setError(t('auth.passkeyCancelled'))
        else if (err.code === 'already_registered') setError(t('auth.passkeyAlreadyRegistered'))
        else if (err.code === 'pending') setError(t('auth.passkeyPending'))
        else setError(err.message)
      } else if (err instanceof ApiError) {
        const detail = String(err.message || err.detail || '')
        if (err.status === 403 || /signup_closed/i.test(detail)) {
          setPhase('closed')
        } else if (/signup_unavailable/i.test(detail)) {
          setError(t('auth.signupUnavailable'))
        } else if (err.status === 429 || /rate limit/i.test(detail)) {
          setError(t('auth.signupRateLimited'))
        } else {
          setError(detail || t('auth.signupFailed'))
        }
      } else {
        setError(t('auth.signupFailed'))
      }
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }

  function goInbox(forceSkip = false) {
    if (!codesAck && !forceSkip) {
      setSkipWarned(true)
      return
    }
    navigate('/inbox?sec=add', { replace: true })
  }

  if (phase === 'loading') {
    return (
      <section className="welcome card">
        <p className="muted">{t('auth.working')}</p>
      </section>
    )
  }

  if (phase === 'closed') {
    return (
      <section className="welcome card">
        <h1>{t('auth.signupTitle')}</h1>
        <p className="lede">{t('auth.signupClosed')}</p>
        <p className="muted">
          <Link to="/passkey">{t('welcome.continuePasskey')}</Link>
          {' · '}
          <Link to="/welcome">{t('auth.backWelcome')}</Link>
        </p>
      </section>
    )
  }

  if (phase === 'codes') {
    return (
      <section className="welcome card">
        <h1>{t('auth.signupRecoveryTitle')}</h1>
        <p className="lede">{t('auth.signupRecoveryLede')}</p>
        {codes ? (
          <div className="codes-box">
            <p className="warn">{t('auth.codesOnce')}</p>
            <ul className="codes-list">
              {codes.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
            <label className="field" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={codesAck}
                onChange={(ev) => {
                  setCodesAck(ev.target.checked)
                  if (ev.target.checked) setSkipWarned(false)
                }}
              />
              <span>{t('auth.signupCodesAck')}</span>
            </label>
          </div>
        ) : (
          <p className="warn">{t('auth.codesFailed')}</p>
        )}
        {skipWarned ? <p className="warn" role="alert">{t('auth.signupSkipWarn')}</p> : null}
        {error ? (
          <p className="warn" role="alert">
            {error}
          </p>
        ) : null}
        <div className="welcome-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!codesAck}
            onClick={() => goInbox(false)}
          >
            {t('auth.signupContinueMail')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => goInbox(true)}>
            {t('auth.signupSkipCodes')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="welcome card">
      <h1>{t('auth.signupTitle')}</h1>
      <p className="lede">{t('auth.signupLede')}</p>
      <form className="auth-form" onSubmit={(e) => void onSubmit(e)}>
        <label className="field">
          <span>{t('auth.emailOptional')}</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder={t('auth.email')}
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('auth.working') : t('auth.signupCreatePasskey')}
        </button>
      </form>
      <EmailCodePanel mode="signup" />
      {error ? (
        <p className="warn" role="alert">
          {error}
          {/signupUnavailable|already|prijavi|sign in/i.test(error) || error === t('auth.signupUnavailable') ? (
            <>
              {' '}
              <Link to="/passkey">{t('welcome.continuePasskey')}</Link>
            </>
          ) : null}
        </p>
      ) : null}
      <p className="muted">
        <Link to="/passkey">{t('welcome.continuePasskey')}</Link>
        {' · '}
        <Link to="/welcome">{t('auth.backWelcome')}</Link>
      </p>
    </section>
  )
}
